import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Remote, UnknownProducer } from "@assets/icons";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import RealTimeStatus from "@common/components/ListDetailViews/common/RealTimeStatus";
import {
  RealTimeItemMonitoring,
  RealTimeMonitoringProps,
} from "@common/components/ListDetailViews/common/RealTimeStatus/type";
import {
  CancelOutlined,
  CheckCircleOutline,
  Edit,
  ErrorOutline,
  HourglassEmpty,
  Description,
  Task,
  EventRepeat,
} from "@mui/icons-material";
import { Avatar } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { ConferenceStatus, MeetingType } from "@services/meetings/types";
import { isSameDay } from "date-fns";
import { convertLocalTime, toHHMMSS } from "@common/utils/localTime.utils";
import { usePolling } from "@common/hooks";
import Typography from "@common/components/Typography";
import ControlPanel from "./ControlPanel";
import {
  canEditMeeting,
  canAssignProducer,
  canProduceMeeting,
} from "@common/utils/user";
import {
  isMeetingInProgress,
  isMeetingInTheFuture,
  isMeetingIsCancelled,
  isRoomSweepMeeting,
} from "@common/utils/meetings";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import UpdateProducer from "./Drawers/UpdateProducer";
import ConfirmUnwrapDialog from "./ConfirmUnwrapDialog";
import WrapUp from "./Drawers/WrapUp";
import { getMonitoringStatusTitle } from "@pages/Assets/Device/DetailView/Tabs/CommonTab/Monitoring";
type MonitoringProps = {};

const Monitoring = (props: MonitoringProps) => {
  const {} = props;
  const { t } = useTranslation();
  const [timer, setTimer] = useState<ReactNode>();
  const [progress, setProgress] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openUnwrapDialog, setOpenUnwrapDialog] = useState<boolean>(false);
  const [openWrapUp, setOpenWrapUp] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const context = React.useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const { meeting, loading } = { ...context.detail };

  const pollTimer = usePolling(() => {
    let time = (meeting?.start as number) - new Date().getTime();
    let [hours, minutes, seconds] = toHHMMSS(time > 0 ? time : 0);
    setTimer(
      <Typography
        realTime
        component="span"
        sx={{ display: "inline" }}
        size="big"
      >
        {hours}
        <Typography component="span" sx={{ display: "inline" }} size="smaller">
          {t("h")}
        </Typography>
        :{minutes}
        <Typography component="span" sx={{ display: "inline" }} size="smaller">
          {t("m")}
        </Typography>
        :{seconds}
        <Typography component="span" sx={{ display: "inline" }} size="smaller">
          {t("s")}
        </Typography>
      </Typography>
    );
  }, 1000);
  const pollProgress = usePolling(
    () => setProgress(new Date().getTime()),
    1000
  );
  useEffect(() => {
    if (meeting) {
      if (
        meeting.status === ConferenceStatus.Scheduled &&
        isSameDay(
          convertLocalTime(new Date(meeting.start as number)),
          new Date()
        )
      )
        pollTimer.start();
      if (
        meeting.status === ConferenceStatus.InProgress ||
        meeting.status === ConferenceStatus.InProgressPartial
      )
        pollProgress.start();
    }
    return () => {
      pollTimer.stop();
      pollProgress.stop();
    };
  }, [meeting]);

  const getMeetingStatusIcon = (): RealTimeMonitoringProps => {
    if (
      meeting?.status !== ConferenceStatus.InProgress &&
      meeting?.status !== ConferenceStatus.InProgressPartial
    ) {
      let res: RealTimeItemMonitoring = {
        icon: <HourglassEmpty />,
        type: "indication",
      };
      switch (meeting?.status) {
        case ConferenceStatus.Scheduled:
          res.icon = <HourglassEmpty />;
          break;
        case ConferenceStatus.Completed:
          res.icon = <CheckCircleOutline />;
          break;
        case ConferenceStatus.Failed:
          res.icon = <ErrorOutline />;
          break;
        case ConferenceStatus.Cancelled:
          res.icon = <CancelOutlined />;
          break;
        default:
          break;
      }
      return { itemsMonitoring: [res] };
    }

    return {
      itemsMonitoring: [
        {
          icon: <Remote />,
          label: t("Controls"),
          type: "button",
          disabled:
            !canEditMeeting(user) ||
            !isMeetingInProgress(meeting) ||
            meeting?.type === MeetingType.AdHoc,
          onClick: () => setOpen(true),
        },
      ],
    };
  };

  const getTimerItem = (): RealTimeMonitoringProps => {
    if (
      meeting?.status === ConferenceStatus.InProgress ||
      meeting?.status === ConferenceStatus.InProgressPartial
    ) {
      return {
        sx: { flexGrow: 1 },
        itemsMonitoring: [
          {
            icon: "",
            type: "progressbar",
            label:
              meeting.end && new Date().getTime() > meeting.end
                ? t("This meeting is in overtime")
                : t("This meeting is in progress"),
            progressbarData: {
              start: meeting.start as number,
              startLabel: t("Start Time"),
              progress: progress,
              end: meeting.end,
              endLabel: t("End Time"),
            },
          },
        ],
      };
    }
    let res: RealTimeItemMonitoring = {
      icon: "",
      type: "label",
    };
    let start = convertLocalTime(new Date(meeting?.start as number));
    let setup = convertLocalTime(new Date(meeting?.setupTime as number));
    switch (meeting?.status) {
      case ConferenceStatus.Scheduled:
        if (isSameDay(start, new Date())) {
          res.label =
            setup.getTime() < new Date().getTime() &&
            new Date().getTime() < start.getTime()
              ? t("This meeting is in setup time for")
              : t("This meeting will start in");
          res.icon = timer;
          return { itemsMonitoring: [res], sx: { minWidth: "12rem" } };
        } else {
          res.label =
            Date.now() <= start.getTime()
              ? t("This meeting will start on")
              : t("This meeting should have started on");
          res.icon =
            start.toLocaleString("en-US", { month: "long" }) +
            " " +
            start.getDate() +
            `, ${t("at")} ` +
            start.toLocaleTimeString("en-US", { timeStyle: "short" });
        }
        break;
      case ConferenceStatus.Completed:
        res.label = t("This meeting is completed");
        break;
      case ConferenceStatus.Failed:
        res.label = t("This meeting failed");
        break;
      case ConferenceStatus.Cancelled:
        res.label = t("This meeting is cancelled");
        break;
      default:
        break;
    }
    return { itemsMonitoring: [res] };
  };

  const getWrapUpItem = (): RealTimeMonitoringProps => {
    let res: RealTimeMonitoringProps = { itemsMonitoring: [] };
    if (
      meeting?.scheduledMeeting?.meetingPurpose == "Sweep" ||
      !canProduceMeeting(user)
    )
      return res;
    res.itemsMonitoring.push({
      icon: meeting?.wrappedUp ? <Task /> : <Description />,
      type: "button",
      onClick: () => {
        if (meeting?.wrappedUp) setOpenUnwrapDialog(true);
        else setOpenWrapUp(true);
      },
      label: meeting?.wrappedUp ? t("Undo Wrap Up") : t("Wrap Up"),
      disabled: isMeetingInTheFuture(meeting) && !canEditMeeting(user),
    });

    return res;
  };

  const getProducerIcon = () => {
    if (meeting?.producer?.logo)
      return (
        <Avatar
          src={__symphony_env__.REACT_APP_SERVER_URL + meeting?.producer?.logo}
        />
      );
    return (
      <span>
        {(
          (meeting?.producer?.firstName?.at(0) ?? "") +
          (meeting?.producer?.lastName?.at(0) ?? "")
        ).toUpperCase()}
      </span>
    );
  };

  const realTimeMonitoringData: RealTimeMonitoringProps[] = [
    {
      ...getMeetingStatusIcon(),
    },
    {
      itemsMonitoring: [
        {
          icon: meeting?.producer ? getProducerIcon() : <UnknownProducer />,
          type: "button",
          disabled: isMeetingIsCancelled(meeting) || !canAssignProducer(user),
          label: t("Producer"),
          tooltip: meeting?.producer
            ? `${meeting?.producer?.firstName} ${meeting?.producer?.lastName}`
            : t("Producer is not assigned"),
          onClick: () => setOpenDrawer(true),
        },
      ],
    },
    { ...getTimerItem() },
    { itemsMonitoring: [] },
    { ...getWrapUpItem() },
  ];

  return (
    <Grid
      ref={ref}
      sx={{
        width: "100%",
      }}>
      {!isRoomSweepMeeting(meeting) ? (
        <>
          {meeting?.wrappedUp && (
            <ConfirmUnwrapDialog
              open={openUnwrapDialog}
              onClose={() => {
                setOpenUnwrapDialog(false);
              }}
            />
          )}
          {!meeting?.wrappedUp && (
            <WrapUp open={openWrapUp} setOpen={setOpenWrapUp} />
          )}
        </>
      ) : (
        <></>
      )}
      <UpdateProducer open={openDrawer} setOpen={setOpenDrawer} />
      <ControlPanel open={open} anchorEl={ref.current} setOpen={setOpen} />
      <Grid sx={{ padding: 2 }}>
        <RealTimeStatus
          loading={loading}
          realTimeMonitoringData={realTimeMonitoringData}
          title={"Monitoring status"}
        />
      </Grid>
    </Grid>
  );
};

export default Monitoring;
